/**
 * Registry models as <SelectField> options — shared by every settings
 * surface that offers a model list (memory extraction, agent editor,
 * agent-list frame).
 */

import {
  opencodePlanBillingHint,
  type ModelOption,
} from "@/lib/model-registry";
import type { SelectFieldOption } from "@/components/ui/select-field";
import { translate } from "@/i18n";
import { settingsAgentDetailMessages } from "@/i18n/messages/settings-agent-detail";

/**
 * Personal OpenCode lists the same model on every plan that serves it, so
 * its options carry plan headers and a Free marker. Every other runtime
 * keeps the vendor suffixed onto the label.
 *
 * `planGrouped` is the same boolean the caller passed to
 * `getModelsForRuntime`, so the labels always match the catalog they came
 * from — deciding it again here could disagree with it.
 */
export function toModelSelectOptions(
  models: ModelOption[],
  planGrouped: boolean,
): SelectFieldOption[] {
  if (!planGrouped) {
    return models.map((m) => ({
      value: m.value,
      label: m.group ? `${m.label} (${m.group})` : m.label,
    }));
  }
  return models.map((m) => ({
    value: m.value,
    label: m.label,
    group: m.group,
    groupHint: opencodePlanBillingHint(m.provider),
    trailing: m.free
      ? translate(settingsAgentDetailMessages, "modelFree")
      : undefined,
  }));
}
